import { useCallback, useEffect, useMemo, useState } from "react";

import { EntityId } from "@reduxjs/toolkit";

import { DateTime } from "luxon";

import { enqueueSnackbar } from "notistack";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { RootState } from "app/store";

import { caseViewerActions } from "./store/slice";
import { playbackSelectors } from "./store/selectors.playback";

import { useRetryFailedDownloadMutation } from "services/streamingApi/caseManager.api";

import { PlayMode } from "features/videoPlayer/types/PlayMode";
import { RecordingPlayer } from "./RecordingPlayer";
import { DossierCapabilityName } from "types/DossierCapabilityName";

export interface ForwardRecordingPlayerProps {
    persistedRecordingId: EntityId;
    originalStartTime: string;
    dossierId: EntityId;
    dossierDeviceId: EntityId;
    dossierDeviceName: string;
    playbackStartDateTime: string | undefined;
    dossierCapabilityNames: DossierCapabilityName[];
}

export function ForwardRecordingPlayer(props: ForwardRecordingPlayerProps) {
    const dispatch = useAppDispatch();

    // get needed store data
    const selectDeviceRecordingWithPlaybackStateById = useMemo(
        () =>
            playbackSelectors.selectDeviceRecordingWithPlaybackStateByIdFactory(),
        [],
    );
    const recordingWithPlaybackState = useAppSelector((state: RootState) =>
        selectDeviceRecordingWithPlaybackStateById(
            state,
            props.persistedRecordingId,
        ),
    );

    const playbackPlaying = useAppSelector(
        playbackSelectors.selectPlaybackPlaying,
    );

    const [retryFailedDownload, { isError }] = useRetryFailedDownloadMutation();

    // determine play mode
    const [playMode, setPlayMode] = useState<PlayMode>("stop");
    useEffect(() => {
        if (playbackPlaying && recordingWithPlaybackState?.inPlaybackWindow) {
            setPlayMode("play");
        } else if (
            (!playbackPlaying &&
                recordingWithPlaybackState?.inPlaybackWindow) ||
            recordingWithPlaybackState?.inBufferWindow
        ) {
            setPlayMode("pause");
        } else {
            setPlayMode("stop");
        }
    }, [
        playbackPlaying,
        recordingWithPlaybackState?.inBufferWindow,
        recordingWithPlaybackState?.inPlaybackWindow,
    ]);

    const tooltipText = useMemo(() => {
        if (!recordingWithPlaybackState) return;

        return `${DateTime.fromISO(recordingWithPlaybackState.persistedStartTime).toLocaleString(DateTime.DATETIME_MED)} - ${DateTime.fromISO(recordingWithPlaybackState.persistedStopTime).toLocaleString(DateTime.DATETIME_MED)} - original: ${DateTime.fromISO(recordingWithPlaybackState.recording.startTime).toLocaleString(DateTime.DATETIME_MED)}`;
    }, [recordingWithPlaybackState]);

    // mark recording as ready
    const onRecordingReadyForPlayback = useCallback(() => {
        dispatch(
            caseViewerActions.setPersistedRecordingReadyForPlayback({
                persistedRecordingId: props.persistedRecordingId,
            }),
        );
    }, [dispatch, props.persistedRecordingId]);

    // mark recording waiting for data
    const onStreamWaitingForData = useCallback(() => {
        dispatch(
            caseViewerActions.setPersistedRecordingWaiting({
                persistedRecordingId: props.persistedRecordingId,
            }),
        );
    }, [dispatch, props.persistedRecordingId]);

    // mark recording failed
    const onPersistedRecordingFailedToLoad = useCallback(() => {
        dispatch(
            caseViewerActions.setPersistedRecordingFailedToLoad({
                persistedRecordingId: props.persistedRecordingId,
            }),
        );
    }, [dispatch, props.persistedRecordingId]);

    const onResetFailedToDownloadRecording = useCallback(() => {
        retryFailedDownload({
            persistedRecordingId: props.persistedRecordingId,
            dossierId: props.dossierId,
        });
    }, [props.persistedRecordingId, props.dossierId]);

    useEffect(() => {
        if (isError) {
            enqueueSnackbar(
                `Failed to queue a retry for the selected recording, pease try again.`,
                {
                    variant: "warning",
                },
            );
        }
    }, [isError]);

    return recordingWithPlaybackState ? (
        <RecordingPlayer
            persistedRecordingId={props.persistedRecordingId}
            originalStartTime={props.originalStartTime}
            dossierDeviceId={props.dossierDeviceId}
            dossierDeviceName={props.dossierDeviceName}
            playbackStartDateTime={props.playbackStartDateTime}
            playMode={playMode}
            recordingWithPlaybackState={recordingWithPlaybackState}
            direction={"forward"}
            onPersistedRecordingReadyForPlayback={onRecordingReadyForPlayback}
            onPersistedRecordingWaiting={onStreamWaitingForData}
            onPersistedRecordingFailedToLoad={onPersistedRecordingFailedToLoad}
            onResetFailedToLoadRecording={() => {
                dispatch(
                    caseViewerActions.resetFailedToLoadRecording({
                        persistedRecordingId: props.persistedRecordingId,
                    }),
                );
            }}
            onResetFailedToDownloadRecording={onResetFailedToDownloadRecording}
            tooltipText={tooltipText}
            currentPlaybackDirection={"forward"}
            hidden={false}
            dossierCapabilityNames={props.dossierCapabilityNames}
        />
    ) : undefined;
}
